<script setup lang="ts">
import { computed } from "vue";
import { formatNumberForScale } from "chartjs-graph";

/**
 * Expected revenue against budget as a half-circle gauge, with a few figures
 * beside it — the Fill & CPM tab's summary card, for the year or for one
 * month by day. Green on budget, orange under, blue over: the same reading as
 * every delta below it.
 */
const props = defineProps<{
	revenue: number;
	budget: number;
	/** "Expected revenue · Amsterdam" — shown above the figure. */
	title: string;
	/** "against budget €412K · Open inventory" — what the gauge is measured against. */
	context: string;
	stats: { label: string; value: string }[];
	/**
	 * Does this card say when edits are waiting to be applied? Only such a card
	 * has room for the pending chip; one that leaves it out keeps its height.
	 */
	tracksPending?: boolean;
	/** Edits are waiting to be applied, so the figures are the last applied ones — said under them. */
	pending?: boolean;
}>();

const arcPath = (t0: number, t1: number): string => {
	const cx = 75;
	const cy = 72;
	const r = 58;
	let d = "";

	for (let i = 0; i <= 40; i++) {
		const t = t0 + (t1 - t0) * (i / 40);
		const angle = ((180 - 180 * t) * Math.PI) / 180;
		const x = cx + r * Math.cos(angle);
		const y = cy - r * Math.sin(angle);
		d += `${i ? "L" : "M"} ${x.toFixed(2)} ${y.toFixed(2)} `;
	}

	return d;
};

const backgroundPath = arcPath(0, 1);

const gauge = computed(() => {
	const gp = props.budget > 0 ? props.revenue / props.budget : 0;
	const onBudget = Math.abs(gp - 1) < 0.002;
	const delta = props.revenue - props.budget;

	return {
		pct: `${Math.round(gp * 100)}%`,
		// Drawn on a scale running to 115%, so over budget still shows as over.
		valuePath: arcPath(0, Math.max(0.001, Math.min(1.15, gp)) / 1.15),
		ink: onBudget
			? "rgb(var(--v-theme-success))"
			: gp < 1
				? "rgb(var(--v-theme-warning))"
				: "rgb(var(--v-theme-info))",
		revenue: formatNumberForScale("money", props.revenue),
		delta: onBudget
			? "At budget"
			: `${delta >= 0 ? "+" : ""}${formatNumberForScale("money", delta)}`,
	};
});
</script>

<template>
	<div class="d-flex align-center flex-wrap ga-5 gauge-card">
		<div class="gauge">
			<svg width="150" height="92" viewBox="0 0 150 92" style="overflow: visible; display: block">
				<path
					:d="backgroundPath"
					fill="none"
					stroke="rgba(var(--v-theme-on-surface), 0.16)"
					stroke-width="14"
					stroke-linecap="round"
				/>
				<path
					:d="gauge.valuePath"
					fill="none"
					:stroke="gauge.ink"
					stroke-width="14"
					stroke-linecap="round"
				/>
			</svg>
			<div class="gauge-pct">{{ gauge.pct }}</div>
		</div>
		<div class="gauge-revenue">
			<div class="text-caption text-uppercase text-medium-emphasis">{{ title }}</div>
			<div class="gauge-revenue-value" :style="{ color: gauge.ink }">
				{{ gauge.revenue }}
			</div>
			<div class="text-caption text-medium-emphasis">
				{{ gauge.delta }} {{ context }}
			</div>
		</div>
		<div class="d-flex flex-column ga-3">
			<div class="d-flex ga-6 flex-wrap">
				<div v-for="stat in stats" :key="stat.label">
					<div class="text-caption text-medium-emphasis mb-1">{{ stat.label }}</div>
					<div class="gauge-stat">{{ stat.value }}</div>
				</div>
			</div>
			<!--
				Always laid out, only hidden: the card centres its contents, so a
				chip that came and went would shift every figure beside it.
			-->
			<v-chip
				v-if="tracksPending"
				color="warning"
				size="small"
				class="align-self-start"
				:style="{ visibility: pending ? 'visible' : 'hidden' }"
				:aria-hidden="!pending"
			>
				Most values update once you apply your changes
			</v-chip>
		</div>
	</div>
</template>

<style scoped lang="scss">
.gauge-card {
	background: rgb(var(--v-theme-surface));
	border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	border-radius: 10px;
	padding: 14px 20px;
}

.gauge {
	position: relative;
	width: 150px;
	height: 92px;
	flex: 0 0 auto;
}

.gauge-pct {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 6px;
	text-align: center;
	font-size: 21px;
	font-weight: 700;
	pointer-events: none;
	font-variant-numeric: tabular-nums;
}

.gauge-revenue {
	border-left: 1px solid rgba(var(--v-theme-on-surface), 0.12);
	padding-left: 20px;
	flex: 1 1 240px;
	min-width: 0;
}

.gauge-revenue-value {
	font-size: 24px;
	font-weight: 700;
	margin: 3px 0 2px;
	font-variant-numeric: tabular-nums;
}

.gauge-stat {
	font-size: 17px;
	font-weight: 650;
	font-variant-numeric: tabular-nums;
}
</style>
